import { forwardRef, useEffect, useMemo, useState } from "react";
import { PERSONAL_DATA_VALIDATION_ENDPOINT } from "../../api/endpoints";
import {
    getBasicPersonalDataCalculatedValues,
    submitBasicPersonalData,
} from "../../api/forms/personal-data";
import { BasicPersonalData, Gender } from "../../api/forms/personal-data/types";
import { Nullable } from "../../api/types";
import { fetcher } from "../../api/utils";
import useDataInputForm, { FORM_IDS } from "../../hooks/useDataInputForm";
import useFeatureFlags from "../../hooks/useFeatureFlags";
import useImperativeFormSubmit from "../../hooks/useImperativeFormSubmit";
import useTestId from "../../hooks/useTestId";
import { FormHandle, InputTypes } from "../../types";
import { generateSelectOptions, toSnakeCase } from "../../utils";
import Error from "../Error";
import DataInputGenericForm, {
    DataInputGenericFormProps,
} from "./DataInputGenericForm";

export const GENDER_TEXTS = {
    [Gender.MALE]: "Male",
    [Gender.FEMALE]: "Female",
};

// Helper function to convert inches to feet and inches (e.g., 85 inches -> {feet: 7, inches: 1})
function convertInchesToFeetInches(height: number | null | undefined): {
    feet: number | null;
    inches: number | null;
} {
    if (!height && height !== 0) return { feet: null, inches: null };

    const feet = Math.floor(height / 12);
    const inches = height % 12;
    // round to 1 decimal place
    const roundedInches = inches !== null ? Math.round(inches * 10) / 10 : null;
    return { feet, inches: roundedInches };
}

export default forwardRef<
    FormHandle<Nullable<BasicPersonalData>> | null,
    {
        setErrors: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
        triggerPrespirometryUpdate?: () => Promise<void>;
    }
>(function BasicPersonalDataForm(props, ref) {
    const testId = useTestId();
    const { featureFlags } = useFeatureFlags();
    const { formData, loading, error, setFromData } = useDataInputForm({
        formId: FORM_IDS.PERSONAL_DATA,
    });
    const [formValues, setFormValues] = useState<
        BasicPersonalData | null | undefined
    >();
    const [validationErrors, setValidationErrors] =
        useState<Partial<Record<keyof BasicPersonalData, string>>>();

    useEffect(() => {
        if (formData) {
            setFormValues(formData as BasicPersonalData);
        }
    }, [formData]);

    const { setErrors } = props;

    const { submissionLoading } = useImperativeFormSubmit<BasicPersonalData>({
        formValues,
        formData,
        ref,
        onSubmitSuccess: (data) => {
            const mergedData = { ...formData } as BasicPersonalData;
            for (const key in data) {
                const _key = key as keyof BasicPersonalData;
                if (data[_key] !== undefined) {
                    (mergedData[_key] as typeof data[typeof _key]) = data[_key];
                }
            }
            return setFromData(mergedData);
        },
        submitFunction: submitBasicPersonalData,
        setErrors: setValidationErrors,
    });

    type FormFields =
        DataInputGenericFormProps<BasicPersonalData>["formFields"];

    const formFields = useMemo<FormFields>(
        (): FormFields =>
            !formValues || !formData
                ? []
                : [
                      {
                          id: "testDate",
                          label: "Test Date",
                          value: formValues.testDate,
                          originalValue: formData.testDate,
                          type: InputTypes.DATE,
                          tooltipText: "Warning: a new test will be created",
                          error: validationErrors?.testDate,
                          required: true,
                      },
                      {
                          id: "firstName",
                          label: "First Name",
                          value: formValues.firstName,
                          originalValue: formData.firstName,
                          type: InputTypes.TEXT,
                          error: validationErrors?.firstName,
                          required: true,
                      },
                      {
                          id: "lastName",
                          label: "Last Name",
                          value: formValues.lastName,
                          originalValue: formData.lastName,
                          type: InputTypes.TEXT,
                          error: validationErrors?.lastName,
                          required: true,
                      },
                      {
                          id: "identification",
                          label: "ID Number",
                          value: formValues.identification,
                          originalValue: formData.identification,
                          tooltipText: "Warning: a new test will be created",
                          type: InputTypes.TEXT,
                          error: validationErrors?.identification,
                          required: true,
                      },
                      {
                          id: "gender",
                          label: "Gender",
                          value: formValues.gender,
                          valueToDisplay: GENDER_TEXTS[formValues.gender],
                          originalValue: formData.gender,
                          type: InputTypes.SELECT,
                          options: generateSelectOptions({
                              enum: Gender,
                              texts: GENDER_TEXTS,
                          }),
                          error: validationErrors?.gender,
                          required: true,
                      },
                      {
                          id: "age",
                          label: "Age (Y)",
                          value: formValues.age,
                          originalValue: formData.age,
                          type: InputTypes.NUMBER,
                          error: validationErrors?.age,
                          validation: {
                              isInteger: true,
                          },
                          required: true,
                      },
                      {
                          id: "weight",
                          label: `Weight (${
                              featureFlags?.foot_pound ? "lb" : "kg"
                          })`,
                          value: formValues.weight,
                          originalValue: formData.weight,
                          type: InputTypes.NUMBER,
                          error: validationErrors?.weight,
                          required: true,
                          validation: {
                              isInteger: true,
                          },
                      },
                      featureFlags?.foot_pound
                          ? {
                                id: "height" as const,
                                label: "Height (ft/in)",
                                value: formValues.height,
                                feetValue: convertInchesToFeetInches(
                                    formValues.height
                                ).feet,
                                inchesValue: convertInchesToFeetInches(
                                    formValues.height
                                ).inches,
                                originalValue: formData.height,
                                type: InputTypes.FEET_INCHES as const,
                                error: validationErrors?.height,
                                required: true as const,
                            }
                          : {
                                id: "height" as const,
                                label: "Height (cm)",
                                value: formValues.height,
                                originalValue: formData.height,
                                type: InputTypes.NUMBER as const,
                                error: validationErrors?.height,
                                required: true as const,
                                validation: {
                                    isInteger: true,
                                },
                            },
                      {
                          id: "bmi",
                          label: "BMI",
                          value: formValues.bmi,
                          originalValue: formData.bmi,
                          type: InputTypes.NUMBER,
                          error: validationErrors?.bmi,
                          required: false,
                          disabled: true,
                      },
                      {
                          id: "fat",
                          label: "%FAT",
                          value: formValues.fat,
                          originalValue: formData.fat,
                          type: InputTypes.NUMBER,
                          error: validationErrors?.fat,
                          required: false,
                          validation: {
                              decimalPlaces: 1,
                          },
                      },
                  ],
        [formValues, formData, validationErrors, featureFlags]
    );

    if (error) {
        return <Error />;
    }

    const validateOnFormValuesChange = async ({
        value,
        fieldSettings: { id: fieldId },
    }: {
        value: Nullable<string | number>;
        fieldSettings: FormFields[number];
    }) => {
        let valueToSave = value;
        const nameFieldIds: FormFields[number]["id"][] = [
            "firstName",
            "lastName",
        ];
        if (nameFieldIds.includes(fieldId)) {
            valueToSave = value?.toString().replace(/\d/g, "");
        }
        const snakeCaseFieldId = toSnakeCase(fieldId as string);

        const validationRes = await fetcher(
            `${PERSONAL_DATA_VALIDATION_ENDPOINT}`,
            {
                method: "POST",
                body: JSON.stringify({
                    person: { [snakeCaseFieldId]: valueToSave },
                }),
            },
            true
        );
        const error = validationRes?.person?.[snakeCaseFieldId]?.error_message;
        const hasError = Boolean(error || !validationRes);

        setValidationErrors((prev) => ({
            ...prev,
            [fieldId]: hasError ? error : undefined,
        }));

        setErrors((prev) => ({
            ...prev,
            [`basicPersonalData_${fieldId}`]: hasError,
        }));

        const newValues = await getBasicPersonalDataCalculatedValues({
            value: valueToSave,
            fieldId: snakeCaseFieldId,
            testId,
        });

        setFormValues((prevFormValues: Nullable<BasicPersonalData>) => {
            if (!prevFormValues) {
                return prevFormValues;
            }

            return {
                ...prevFormValues,
                [fieldId]:
                    newValues?.[snakeCaseFieldId as keyof typeof newValues] ||
                    valueToSave,
                bmi:
                    newValues?.bmi === undefined
                        ? prevFormValues.bmi
                        : newValues.bmi,
            };
        });

        const needToTriggerPrespirometryUpdate = [
            "gender",
            "age",
            "weight",
            "height",
        ].includes(fieldId);

        if (
            needToTriggerPrespirometryUpdate &&
            props.triggerPrespirometryUpdate
        ) {
            await props.triggerPrespirometryUpdate();
        }
    };

    return (
        <DataInputGenericForm
            formTitle="Personal Data"
            formFields={formFields}
            formData={formValues}
            onFromValuesChange={validateOnFormValuesChange}
            loading={loading || submissionLoading}
        />
    );
});
